import React, { forwardRef } from "react";
import { useDensity } from "./context";
import type { Density } from "./context";

/**
 * Card — contenitore condiviso TL4K.
 * Copre il pattern 5 dell'assessment (`bg-white rounded-xl shadow-sm border p-*`),
 * ricorrente in admin-console (6 punti) e qns.
 *
 * Nota di accessibilità: `interactive` NON produce un <div role="button">.
 * Quel pattern è la trappola classica (niente focus nativo, niente
 * attivazione da tastiera, niente stato disabled). Qui `interactive` emette
 * un <button> vero.
 *
 * Se serve una card cliccabile che contiene a sua volta link o bottoni,
 * `interactive` è la scelta sbagliata (annidare interattivi è invalido):
 * usare una card normale con un link "overlay" sul titolo.
 */

export type CardTone = "default" | "warm" | "info" | "success" | "warning" | "error";
export type CardElevation = "flat" | "sm" | "md";

export interface CardProps {
  /** Elemento renderizzato. Usare "section"/"article" quando la card ha un heading. */
  as?: "div" | "section" | "article" | "li" | "aside";
  density?: Density;
  tone?: CardTone;
  elevation?: CardElevation;
  /** Rende la card un <button> nativo. Richiede onClick. */
  interactive?: boolean;
  onClick?: React.MouseEventHandler;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  {
    as = "div",
    density: densityProp,
    tone = "default",
    elevation = "sm",
    interactive = false,
    onClick,
    header,
    footer,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const density = useDensity(densityProp);

  if (import.meta.env.DEV) {
    if (interactive && !onClick) {
      console.warn("[TL4K] <Card interactive> senza onClick: rende un bottone che non fa nulla.");
    }
  }

  const classes = cx(
    "tl4k-card",
    `tl4k-card--${density}`,
    tone !== "default" && `tl4k-card--tone-${tone}`,
    `tl4k-card--${elevation}`,
    interactive && "tl4k-card--interactive",
    className,
  );

  const inner = (
    <>
      {header ? <div className="tl4k-card__header">{header}</div> : null}
      {children}
      {footer ? <div className="tl4k-card__footer">{footer}</div> : null}
    </>
  );

  if (interactive) {
    return (
      <button
        {...rest}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        style={style}
        onClick={onClick}
      >
        {inner}
      </button>
    );
  }

  const Component = as as React.ElementType;
  return (
    <Component {...rest} ref={ref} className={classes} style={style} onClick={onClick}>
      {inner}
    </Component>
  );
});
